<template>
  <div class="select-modal" :readonly="readonly">
    <div v-if="type === 'default'" @click="open" class="select-modal__input">
      <Draggable v-if="hasValue" v-model="valueComp" :disabled="!sortable" class="select-modal__input-draggable" handle=".select-modal__input-item-sort-handle">
        <div v-for="item in valueComp" class="select-modal__input-item">
          <slot name="item" :item="item" scope="input" />
          <Icon v-if="sortable" icon="fa-pro-solid:arrows-up-down" class="select-modal__input-item-sort-handle" />
        </div>
      </Draggable>
      <slot v-if="!hasValue" name="empty" scope="input" />

      <input
        type="text"
        class="select-modal__input-element"
        :value="valueComp.map(v => v.id).join('-')"
        :required="required"
      />
    </div>
    <component :is="modalWrapper" class="select-modal__modal-wrapper">
      <ModalComponent :show.sync="show" clickaway class="select-modal__modal">
        <Card theme="flat" class="select-modal__modal-card">
          <template #default>
            <div v-if="$slots.sidebar" class="select-modal__modal-sidebar">
              <slot name="sidebar" />
            </div>
            <div class="select-modal__modal-content">
              <slot name="header" />
              <div class="card__title">
                <div>{{ title }}</div>
                <div>
                  <CheckboxComponent
                    v-if="selectAll && multiple"
                    v-model="allSelected"
                    :label="$t('selectAll')"
                    style="max-height: 1em"
                  />
                </div>
              </div>

              <InputComponent v-if="hasSearch" class="select-modal__search">
                <input
                  class="elder-input__element"
                  type="search"
                  :placeholder="$t('search') + '...'"
                  v-model="defaultFilter.search"
                />
                <div class="elder-input__icon">
                  <FontAwesomeIcon :icon="['fal', 'search']" />
                </div>
              </InputComponent>
              <LoaderComponent :value="promises.fetch" theme="default">
                <div class="select-modal__items">
                  <div
                    v-for="(item, index) in itemsComp"
                    :key="index"
                    class="select-modal__item"
                    :class="{
                      'select-modal__item--selected': isSelected(item),
                      'select-modal__item--disabled': isDisabled(item),
                    }"
                    @click="select(item)"
                  >
                    <slot name="item" :item="item" :disabled="isDisabled(item)" scope="modal" />
                  </div>
                  <slot v-if="!itemsComp.length" name="noresults" :filter="filterComp" />
                  <slot name="bottom" />
                </div>
              </LoaderComponent>
            </div>
          </template>
          <template #footer>
            <slot name="actions" />
            <ButtonComponent :label="$t('cancel')" :icon="['fal', 'times']" @click="close" data-action="cancel" />
            <ButtonComponent
              :label="$t('confirm')"
              :icon="['fal', 'angle-right']"
              :promise="promise"
              theme="primary"
              :disabled="required && !selected.length"
              data-action="confirm"
              @click="confirm"
            />
          </template>
        </Card>
      </ModalComponent>
    </component>
  </div>
</template>

<script>
import { Debounce } from '@/utils'

import { ModalComponent } from 'vue-elder-modal'
import { CheckboxComponent } from 'vue-elder-checkbox'
import { InputComponent } from 'vue-elder-input'
import { LoaderComponent } from 'vue-elder-loader'
import Draggable from 'vuedraggable'
import Card from '@/components/Card'

export default {
  props: {
    title: {
      type: String,
      default() {
        return this.$t('select')
      },
    },
    search: Boolean,
    multiple: Boolean,
    readonly: Boolean,
    required: Boolean,
    sortable: Boolean,
    selectAll: Boolean,
    type: {
      type: String,
      enum: ['default', 'modal-only'],
      default: 'default',
    },
    filter: {
      type: Object,
      default: () => ({}),
    },
    promise: Promise,
    compare: {
      type: Function,
      default: (a, b) => a.id === b.id,
    },
    isDisabled: {
      type: Function,
      default: () => false,
    },
    value: [Object, Array],
    items: {
      type: [Function, Array],
      required: true,
    },
    modalWrapper: {
      type: [String, Function],
      default: 'div',
    },
  },
  data() {
    return {
      show: false,
      fetchedItems: [],
      promises: {
        fetch: null,
      },
      selected: [],
      defaultFilter: {
        search: null,
      },
    }
  },
  watch: {
    show: {
      handler(val) {
        if (val) {
          this.internalfetch().then(() => {
            this.select(this.valueComp)
          })
          this.$emit('show')
        } else {
          this.selected = []
          this.$emit('close')
        }
      },
      immediate: true,
    },
    filterComp: {
      handler: 'performQuery',
      deep: true,
    },
    promise(val) {
      if (!val) return
      val.then(() => this.close())
    },
  },
  computed: {
    filterComp() {
      return {
        ...this.defaultFilter,
        ...this.filter,
      }
    },
    valueComp: {
      get() {
        if (!this.value) return []
        return this.multiple ? this.value : [this.value]
      },
      set(val) {
        this.$emit('input', val)
      },
    },
    itemsComp() {
      return this.items instanceof Array ? this.items : this.fetchedItems
    },
    hasValue() {
      return this.valueComp.length
    },
    hasSearch() {
      return this.search && typeof this.items === 'function'
    },
    allSelected: {
      get() {
        return this.itemsComp.every(i => this.selected.find(s => this.compare(s, i)))
      },
      set(val) {
        if (!val) return (this.selected = [])

        this.select(this.itemsComp, true)
      },
    },
  },
  methods: {
    performQuery: Debounce(function () {
      this.internalfetch()
    }, 200),
    select(items, state) {
      items = items instanceof Array ? items : [items]
      items.forEach(item => {
        if (this.isDisabled(item)) return
        if ((state === undefined && this.isSelected(item)) || state === false)
          return (this.selected = this.selected.filter(i => !this.compare(i, item)))
        if ((state === undefined && !this.isSelected(item)) || state === true) {
          if (!this.multiple) this.selected = []
          this.selected.push(item)
          return
        }
      })
      this.$emit('select', this.selected)
    },
    isSelected(item) {
      return this.selected.find(i => this.compare(i, item))
    },
    open() {
      if (this.readonly) return
      this.show = true
    },
    close() {
      this.show = false
    },
    confirm() {
      this.$emit('input', this.multiple ? this.selected : this.selected.shift() || null)

      if (!('promise' in this.$options.propsData)) this.close()
    },
    internalfetch() {
      if (this.items instanceof Array) return Promise.resolve(this.items)
      return (this.promises.fetch = this.items(this.filterComp).then(data => (this.fetchedItems = data)))
    },
  },
  components: {
    Card,
    ModalComponent,
    InputComponent,
    LoaderComponent,
    CheckboxComponent,
    Draggable
  },
}
</script>

<style lang="scss">
.select-modal {
  &__input {
    cursor: pointer;
    position: relative;
    height: 100%;
    width: 100%;

    max-height: 450px;
    overflow-y: auto;

    @include scrollShadow();

    &-element {
      position: absolute;
      width: 100%;
      height: 100%;
      margin: 0 !important;
      top: 0;
      opacity: 0;
      pointer-events: none;
      outline: none !important;
    }

    &-draggable {
      display: flex;
      flex-direction: column;
      gap: .5rem;
    }

    &-item {
      display: grid;
      grid-template-columns: 1fr auto;
      align-items: center;
      gap: .5rem;

      &:hover .select-modal__input-item-sort-handle {
        opacity: 1;
      }
    }
    
    &-item-sort-handle {
      opacity: 0;
      transition: opacity 100ms ease-in;
    }
   
  }

  &__modal-card {
    & > .card__content {
      display: flex;
    }
  }

  &__modal-sidebar {
    $spacing: 2rem;
    padding-right: $spacing;
    margin-right: $spacing;
    border-right: 1px solid var(--border-color);
  }

  &__modal-content {
    flex-grow: 1;
    .elder-checkbox {
      &__label {
        font-style: italic;
        color: css-alpha('dark', 0.85);
      }
    }
  }

  &__search {
    margin-bottom: 1rem;

    .elder-input__icon {
      padding-right: 1.1em;
    }
  }

  .card__title {
    display: flex;
    justify-content: space-between;
    align-items: end;
    gap: 1rem;
    font-size: 1.2rem;
    margin-bottom: 1rem;
  }

  .card__footer {
    & > div {
      justify-content: center;
    }
  }

  &__items {
    max-height: 450px;
    overflow-y: scroll;

    @include scrollShadow();
  }

  &__item {
    cursor: pointer;
    padding: 0.5rem;
    transition: background-color 150ms ease, border-color 150ms ease;
    border-radius: var(--border-radius);
    border: 1px solid transparent;

    &:hover {
      background-color: css-alpha('border-color', 0.3);
      border-color: css-alpha('border-color', 0.3);
    }

    &--selected {
      background-color: css-alpha('primary', 0.1) !important;
      border-color: css-alpha('primary', 1) !important;
    }

    &--disabled {
      cursor: not-allowed;
    }

    & + * {
      margin-top: 0.2rem;
    }
  }
}
</style>
